import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { getGetStoreSettingsQueryKey, useGetStoreSettings, useUpdateStoreSettings } from "@/api";
import { useSavedFlash } from "@/hooks/settings/useSavedFlash";
import { getApiErrorMessage } from "@/lib/api/errors";
import {
  SHIPPING_METHOD_KEYS,
  SHIPPING_METHOD_META,
  SMS_OPTION_KEYS,
  SMS_OPTION_META,
  type ShippingMethodKey,
} from "@/lib/settings/constants";
import {
  shippingSettingsSchema,
  type ShippingSettingsFormValues,
} from "@/lib/validation/settings.schemas";

const methodField = (key: ShippingMethodKey) => `shipping_${key}` as const;

/** Shipping tab: delivery methods and automatic SMS. Online payment is always Zarinpal with split. */
export function useSettingsShipping(storeId: number | null, enabled: boolean) {
  const queryClient = useQueryClient();
  const { saved, flash } = useSavedFlash();
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading } = useGetStoreSettings(storeId ?? 0, {
    query: { enabled: enabled && storeId !== null },
  });
  const settings = data?.status === 200 ? data.data.data : undefined;

  const form = useForm<ShippingSettingsFormValues>({
    resolver: zodResolver(shippingSettingsSchema),
    defaultValues: {
      shipping_post: true,
      shipping_courier: true,
      shipping_pickup: false,
      sms_order_confirm: true,
      sms_tracking: true,
      sms_status_update: false,
    },
  });

  useEffect(() => {
    if (!settings) return;
    const methods = settings.shipping_methods as Record<string, unknown> | undefined;
    form.reset({
      shipping_post: Boolean(methods?.post),
      shipping_courier: Boolean(methods?.courier),
      shipping_pickup: Boolean(methods?.pickup),
      sms_order_confirm: settings.sms_order_confirm,
      sms_tracking: settings.sms_tracking,
      sms_status_update: settings.sms_status_update,
    });
  }, [settings, form]);

  const values = useWatch({ control: form.control }) as ShippingSettingsFormValues;

  const { mutate: updateSettings, isPending: isSubmitting } = useUpdateStoreSettings({
    mutation: {
      onSuccess: () => {
        if (storeId !== null) {
          void queryClient.invalidateQueries({ queryKey: getGetStoreSettingsQueryKey(storeId) });
        }
        flash();
      },
      onError: (error) =>
        setSaveError(getApiErrorMessage(error) ?? "ذخیره تنظیمات انجام نشد؛ دوباره تلاش کنید"),
    },
  });

  const onSubmit = form.handleSubmit((formValues) => {
    if (storeId === null) return;
    setSaveError(null);
    updateSettings({
      storeId,
      data: {
        sms_order_confirm: formValues.sms_order_confirm,
        sms_tracking: formValues.sms_tracking,
        sms_status_update: formValues.sms_status_update,
        shipping_methods: {
          post: formValues.shipping_post,
          courier: formValues.shipping_courier,
          pickup: formValues.shipping_pickup,
        },
      },
    });
  });

  const toggleMethod = useCallback(
    (key: ShippingMethodKey) => {
      const field = methodField(key);
      form.setValue(field, !form.getValues(field), { shouldDirty: true });
      void form.trigger("shipping_post");
    },
    [form],
  );

  const shippingOptions = useMemo(
    () =>
      SHIPPING_METHOD_KEYS.map((key) => ({
        key,
        title: SHIPPING_METHOD_META[key].titleKey,
        description: SHIPPING_METHOD_META[key].descKey,
        checked: Boolean(values?.[methodField(key)]),
        onToggle: () => toggleMethod(key),
      })),
    [toggleMethod, values],
  );

  const smsOptions = useMemo(
    () =>
      SMS_OPTION_KEYS.map((key) => ({
        key,
        title: SMS_OPTION_META[key].titleKey,
        description: SMS_OPTION_META[key].descKey,
        checked: Boolean(values?.[key]),
        onToggle: (checked: boolean) => form.setValue(key, checked, { shouldDirty: true }),
      })),
    [form, values],
  );

  const hasMethod = Boolean(
    values?.shipping_post || values?.shipping_courier || values?.shipping_pickup,
  );

  return {
    onSubmit,
    shippingOptions,
    smsOptions,
    methodsError: hasMethod ? null : "حداقل یک روش ارسال باید فعال باشد",
    canSubmit: hasMethod && !isSubmitting,
    isLoading,
    isSubmitting,
    saved,
    saveError,
  };
}

export type SettingsShippingState = ReturnType<typeof useSettingsShipping>;
